import React, { useState, useEffect, useMemo } from "react";
import { Calendar as BigCalendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "moment/locale/fr";
import Modal from "react-modal";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import apiClient from "../Api/apiConfig";
import { useAuth } from "../lib/auth";

import Clock from "react-clock";
import "react-clock/dist/Clock.css";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "../styles/ReservationPage.css";

moment.locale("fr");
Modal.setAppElement("#root");

const localizer = momentLocalizer(moment);

const messages = {
  allDay: "Toute la journée",
  previous: "Précédent",
  next: "Suivant",
  today: "Aujourd'hui",
  month: "Mois",
  week: "Semaine",
  day: "Jour",
  agenda: "Agenda",
  date: "Date",
  time: "Heure",
  event: "Événement",
  noEventsInRange: "Aucun événement à cette période.",
  showMore: (total) => `+ ${total} supplémentaire(s)`,
};

const formats = {
  timeGutterFormat: (date, culture, localizer) =>
    localizer.format(date, "HH:mm", culture),
  dayHeaderFormat: "dddd DD MMMM YYYY",
  dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
    `${localizer.format(start, "DD MMM", culture)} - ${localizer.format(
      end,
      "DD MMM",
      culture
    )}`,
};

const ReservationPage = () => {
  const { user } = useAuth();

  // -- ÉTATS ----------------------------------------------------
  const [events, setEvents] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [calendarSyncModalOpen, setCalendarSyncModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(true); // Modal d'info affiché au chargement
  const [appointmentType, setAppointmentType] = useState("call");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [formData, setFormData] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    email: user?.email || "",
    phone: "",
  });
  const [currentTime, setCurrentTime] = useState(new Date());

  // 🔑 Client ID PayPal (chargé depuis le backend)
  const [paypalClientId, setPaypalClientId] = useState(null);
  const [isLoadingPaypal, setIsLoadingPaypal] = useState(true);
  const [paypalError, setPaypalError] = useState("");

  // -- CONSTANTES & FONCTIONS UTILITAIRES -----------------------
  const BASE_PRICE = 150;
  const getPrice = () => {
    if (appointmentType === "bedbug") {
      const isMember = user?.role === "member";
      const discount = isMember ? 0.2 : 0;
      return BASE_PRICE - BASE_PRICE * discount;
    }
    return 0;
  };

  // Options PayPal mémorisées
  const paypalOptions = useMemo(() => {
    if (!paypalClientId) return null;
    return {
      "client-id": paypalClientId,
      currency: "EUR",
    };
  }, [paypalClientId]);

  // -- EFFET : CHARGEMENT DES RENDEZ-VOUS & HORLOGE -------------
  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        const response = await apiClient.get("/api/appointments");
        const appointments = response.data;
        setEvents(
          appointments.map((appointment) => ({
            start: new Date(appointment.start_time),
            end: new Date(appointment.end_time),
            title:
              appointment.appointment_type === "call"
                ? "Appel téléphonique"
                : "Intervention à domicile",
            id: appointment.id,
          }))
        );
      } catch (error) {
        console.error("Erreur lors de la récupération des rendez-vous :", error);
      }
    };

    fetchAppointments();

    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, [user]);

  // -- EFFET : CHARGEMENT DU CLIENT-ID PAYPAL -------------------
  useEffect(() => {
    const fetchClientId = async () => {
      try {
        // adapte selon ton apiClient (baseURL + chemin)
        const res = await apiClient.get("/api/paypal/client-id");
        const data = res.data;

        if (!data.clientId) {
          throw new Error("Client ID PayPal manquant dans la réponse serveur.");
        }

        setPaypalClientId(data.clientId);
      } catch (err) {
        console.error("❌ Erreur chargement client-id PayPal :", err);
        setPaypalError(
          "Impossible de charger PayPal pour le moment. Veuillez réessayer plus tard."
        );
      } finally {
        setIsLoadingPaypal(false);
      }
    };

    fetchClientId();
  }, []);

  // -- GESTION DE LA SÉLECTION D'UN CRÉNEAU ---------------------
  const handleSlotSelect = (slotInfo) => {
    const now = new Date();
    if (new Date(slotInfo.start) < now) {
      alert("Vous ne pouvez pas réserver un créneau dans le passé.");
      return;
    }

    // Vérifier s'il existe un conflit avec un rendez-vous existant
    const hasConflict = events.some(
      (event) =>
        event.start.getTime() === slotInfo.start.getTime() &&
        event.end.getTime() === slotInfo.end.getTime()
    );
    if (hasConflict) {
      alert("Un rendez-vous existe déjà pour ce créneau horaire.");
      return;
    }

    setSelectedSlot(slotInfo);
    setModalIsOpen(true);
  };

  // -- GESTION DE LA CONFIRMATION DU RENDEZ-VOUS ----------------
  const handleConfirm = async (syncToCalendar = false, orderID = null) => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      if (!user) {
        throw new Error("Vous devez être connecté pour réserver un rendez-vous.");
      }

      if (!selectedSlot) {
        throw new Error("Aucun créneau sélectionné.");
      }

      if (!formData.last_name || !formData.first_name || !formData.email) {
        throw new Error("Veuillez renseigner nom, prénom et email.");
      }

      const payload = {
        user_id: user.id,
        start_time: moment(selectedSlot.start).format("YYYY-MM-DD HH:mm:ss"),
        end_time: moment(selectedSlot.end).format("YYYY-MM-DD HH:mm:ss"),
        appointment_type: appointmentType,
        ...formData,
        sync_to_calendar: syncToCalendar,
        ...(appointmentType === "bedbug" && orderID
          ? { paypal_order_id: orderID }
          : {}),
      };

      console.log("📤 Envoi de la réservation /api/appointments :", payload);

      const response = await apiClient.post("/api/appointments", payload);
      if (response.status === 201) {
        alert("Votre rendez-vous a été confirmé avec succès.");
        window.location.reload();
      } else {
        throw new Error("Erreur lors de la confirmation du rendez-vous.");
      }
    } catch (error) {
      console.error("Erreur lors de la réservation :", error);
      alert(error.message || "Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
      setModalIsOpen(false);
    }
  };

  // -- GESTION DE L'ANNULATION D'UN RENDEZ-VOUS -----------------
  const handleCancelAppointment = async (appointmentId) => {
    try {
      await apiClient.delete(`/api/appointments/${appointmentId}`);
      setEvents((prevEvents) =>
        prevEvents.filter((event) => event.id !== appointmentId)
      );
      alert("Rendez-vous annulé avec succès.");
      setSelectedEvent(null);
    } catch (error) {
      console.error("Erreur lors de l'annulation :", error);
      alert("Une erreur est survenue.");
    }
  };

  // -- GESTION DES CHAMPS DE FORMULAIRE -------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // -- RENDU ----------------------------------------------------
  return (
    <div className="reservation-page">
      {/* Modal d'information affiché au chargement */}
      <Modal
        isOpen={isInfoModalOpen}
        onRequestClose={() => setIsInfoModalOpen(false)}
        overlayClassName="modal-overlay"
        className="modal"
      >
        <h3>Bienvenue sur notre service de réservation</h3>
        <p>
          Ce service vous permet de réserver facilement un rendez-vous en ligne
          pour :
        </p>
        <ul>
          <li>
            Obtenir des conseils et informations personnalisés lors d&apos;un{" "}
            <strong>appel téléphonique</strong>.
          </li>
          <li>
            Planifier une <strong>intervention à domicile</strong> selon vos
            besoins.
          </li>
        </ul>
        <p>
          Pour prendre un rendez-vous, il vous suffit de cliquer sur la date et
          l&apos;heure souhaitées. Une fenêtre s&apos;ouvrira pour finaliser
          votre réservation. Une fois confirmé, vous recevrez un e-mail de
          confirmation.
        </p>
        <button
          className="button"
          onClick={() => {
            console.log("Bouton Compris cliqué");
            setIsInfoModalOpen(false);
          }}
        >
          Compris
        </button>
      </Modal>

      {/* Barre latérale */}
      <aside className="sidebar" style={{ width: "15%" }}>
        <div className="clock-container">
          <Clock value={currentTime} />
        </div>
        <div className="price-info">
          <h4 style={{ color: "white" }}>Tarifs et réductions</h4>
          <p style={{ color: "white" }}>
            Nos tarifs varient en fonction du service choisi. <br />
            <strong>Exemple :</strong> 150€ pour un appel téléphonique. <br />
            Des réductions sont prévues pour nos adhérents.
          </p>
          <button
            onClick={() => setIsInfoModalOpen(true)}
            className="info-button"
          >
            Plus d&apos;infos sur le fonctionnement
          </button>
        </div>
      </aside>

      {/* Zone principale : Calendrier */}
      <main className="main-content">
        <h1>Réservez un créneau</h1>
        <div className="calendar-container">
          <BigCalendar
            localizer={localizer}
            events={events}
            selectable
            views={["week"]}
            defaultView="week"
            step={60}
            timeslots={1}
            min={new Date(2025, 0, 1, 8, 0)}
            max={new Date(2025, 0, 1, 17, 0)}
            formats={formats}
            messages={messages}
            onSelectSlot={handleSlotSelect}
            onSelectEvent={(event) => setSelectedEvent(event)}
            style={{ cellHeight: "100px" }}
          />
        </div>
      </main>

      {/* Modal de réservation */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={() => setModalIsOpen(false)}
        overlayClassName="modal-overlay"
        className="modal"
      >
        <h3>Confirmez votre réservation</h3>
        {selectedSlot && (
          <>
            <p>
              <strong>Date :</strong>{" "}
              {moment(selectedSlot.start).format("dddd DD/MM/YYYY")}
            </p>
            <p>
              <strong>Heure :</strong>{" "}
              {`${moment(selectedSlot.start).format("HH:mm")} - ${moment(
                selectedSlot.end
              ).format("HH:mm")}`}
            </p>
          </>
        )}
        <label>Nom :</label>
        <input
          type="text"
          name="last_name"
          value={formData.last_name}
          onChange={handleChange}
        />
        <label>Prénom :</label>
        <input
          type="text"
          name="first_name"
          value={formData.first_name}
          onChange={handleChange}
        />
        <label>Email :</label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
        />
        <label>Téléphone :</label>
        <input
          type="text"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
        />
        <label>Type :</label>
        <select
          value={appointmentType}
          onChange={(e) => setAppointmentType(e.target.value)}
        >
          <option value="call">Appel téléphonique</option>
          <option value="bedbug">Intervention à domicile</option>
        </select>

        {/* Message erreur PayPal éventuel */}
        {paypalError && appointmentType === "bedbug" && (
          <p style={{ color: "red", marginTop: "10px" }}>{paypalError}</p>
        )}

        {/* Cas 1 : Appel téléphonique => synchro Google optionnelle */}
        {appointmentType === "call" && (
          <div className="modal-actions">
            <button
              className="button"
              onClick={() => setCalendarSyncModalOpen(true)}
              disabled={isSubmitting || isPaying}
            >
              Confirmer
            </button>
          </div>
        )}

        {/* Cas 2 : Intervention à domicile => PayPal + synchro (optionnel) */}
        {appointmentType === "bedbug" && (
          <>
            <p>
              <strong>Tarif :</strong> {getPrice().toFixed(2)} €
            </p>

            {isLoadingPaypal && (
              <p
                style={{
                  textAlign: "center",
                  marginTop: "10px",
                  color: "#666",
                }}
              >
                Chargement de PayPal…
              </p>
            )}

            {!isLoadingPaypal && paypalOptions && !paypalError && (
              <>
                <PayPalScriptProvider options={paypalOptions}>
                  <PayPalButtons
                    style={{ layout: "vertical" }}
                    createOrder={(data, actions) => {
                      const price = getPrice();

                      if (
                        !formData.last_name ||
                        !formData.first_name ||
                        !formData.email
                      ) {
                        alert(
                          "Veuillez renseigner nom, prénom et email avant de procéder au paiement."
                        );
                        return Promise.reject(
                          new Error("Formulaire incomplet pour le paiement.")
                        );
                      }

                      if (!price || price <= 0) {
                        alert("Montant invalide pour ce rendez-vous.");
                        return Promise.reject(new Error("Prix invalide."));
                      }

                      setIsPaying(true);

                      return actions.order.create({
                        purchase_units: [
                          {
                            amount: {
                              value: price.toFixed(2),
                              currency_code: "EUR",
                            },
                          },
                        ],
                      });
                    }}
                    onApprove={async (data, actions) => {
                      try {
                        const order = await actions.order.capture();
                        console.log(
                          "✅ Paiement capturé (intervention à domicile) :",
                          order
                        );
                        const orderId = order.id || data.orderID;
                        // On confirme le rendez-vous sans synchro Google
                        await handleConfirm(false, orderId);
                      } catch (err) {
                        console.error(
                          "❌ Erreur lors de la capture ou de la confirmation :",
                          err
                        );
                        alert(
                          "Une erreur est survenue lors du paiement ou de la réservation."
                        );
                      } finally {
                        setIsPaying(false);
                      }
                    }}
                    onCancel={() => {
                      console.log(
                        "ℹ️ Paiement PayPal annulé pour intervention à domicile."
                      );
                      setIsPaying(false);
                    }}
                    onError={(err) => {
                      console.error(
                        "❌ Erreur PayPal (intervention) :",
                        err
                      );
                      setIsPaying(false);
                      alert(
                        "Une erreur est survenue avec PayPal. Veuillez réessayer."
                      );
                    }}
                    disabled={isSubmitting || isPaying}
                  />
                </PayPalScriptProvider>
                {isPaying && (
                  <div className="spinner">
                    Paiement en cours, veuillez patienter...
                  </div>
                )}
              </>
            )}
          </>
        )}

        <div className="modal-actions">
          <button
            className="button close-button"
            onClick={() => setModalIsOpen(false)}
            disabled={isSubmitting || isPaying}
          >
            Annuler
          </button>
        </div>
      </Modal>

      {/* Modal de synchronisation Google Calendar */}
      <Modal
        isOpen={calendarSyncModalOpen}
        onRequestClose={() => setCalendarSyncModalOpen(false)}
        overlayClassName="modal-overlay"
        className="modal"
      >
        <h3>Synchronisation avec Google Calendar</h3>
        <p>Souhaitez-vous ajouter ce rendez-vous à votre Google Calendar ?</p>
        <div className="modal-actions">
          <button
            className="button"
            onClick={() => {
              console.log("Bouton Oui cliqué");
              setCalendarSyncModalOpen(false);
              handleConfirm(true);
            }}
            disabled={isSubmitting || isPaying}
          >
            Oui
          </button>
          <button
            className="button"
            onClick={() => {
              console.log("Bouton Non cliqué");
              setCalendarSyncModalOpen(false);
              handleConfirm(false);
            }}
            disabled={isSubmitting || isPaying}
          >
            Non
          </button>
        </div>
      </Modal>

      {/* Modal de visualisation / annulation d'un rendez-vous existant */}
      <Modal
        isOpen={!!selectedEvent}
        onRequestClose={() => setSelectedEvent(null)}
        overlayClassName="modal-overlay"
        className="modal"
      >
        {selectedEvent && (
          <>
            <h3>Détails de l'événement</h3>
            <p>
              <strong>Date :</strong>{" "}
              {moment(selectedEvent.start).format("dddd DD/MM/YYYY")}
            </p>
            <p>
              <strong>Heure :</strong>{" "}
              {`${moment(selectedEvent.start).format(
                "HH:mm"
              )} - ${moment(selectedEvent.end).format("HH:mm")}`}
            </p>
            <p>
              <strong>Type :</strong> {selectedEvent.title}
            </p>
            <div className="modal-actions">
              <button
                className="button close-button"
                onClick={() =>
                  handleCancelAppointment(selectedEvent.id)
                }
              >
                Annuler
              </button>
              <button
                className="button"
                onClick={() => setSelectedEvent(null)}
              >
                Fermer
              </button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default ReservationPage;
